import { MouseEventHandler, PropsWithChildren, useRef } from 'react';

import ChevronLeftIcon from '@flipster/react/icon/generated/ChevronLeftIcon';
import { useDragScroll } from '@flipster/react/misc/browser/drag-scroll';
import { useIsElementInViewport } from '@flipster/react/misc/browser/intersection';
import { useCheckScrollable } from '@flipster/react/misc/browser/scrollable';
import { css } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';

export const RightLayout = ({ children }: PropsWithChildren) => {
  const scrollRef = useRef<HTMLDListElement>(null);

  const mouseEvents = useDragScroll(scrollRef);
  const isScrollable = useCheckScrollable('horizontal', scrollRef);
  const [leftRef, isVisibleLeft] = useIsElementInViewport<HTMLDivElement>();
  const [rightRef, isVisibleRight] = useIsElementInViewport<HTMLDivElement>();

  const onClickScroll: MouseEventHandler<HTMLButtonElement> = (e) => {
    if (!scrollRef.current) return;
    const scrollSize = 160;
    const isLeft = e.currentTarget.value === 'left';
    const maxScrollLeft = scrollRef.current.scrollWidth - scrollRef.current.clientWidth;
    let leftPoint = Math.round(scrollRef.current.scrollLeft + scrollSize * (isLeft ? -1 : 1));
    if (!isLeft) {
      leftPoint = Math.min(leftPoint, maxScrollLeft);
    } else if (leftPoint < 10) {
      leftPoint = 0;
    }
    scrollRef.current.scrollTo({ left: leftPoint, behavior: 'smooth' });
  };

  return (
    <div className={hstack({ pos: 'relative', flex: 1, overflowX: 'hidden' })}>
      {isScrollable && !isVisibleLeft && (
        <button type='button' value='left' onClick={onClickScroll} className={css(scrollArrowCSS, { left: 0 })}>
          <ChevronLeftIcon size={16} />
        </button>
      )}
      <dl
        className={css({
          mt: { base: 8, tablet: 2 }, // 하단 높이 맞추기 위한 css
          overflowX: 'auto',
          whiteSpace: 'nowrap',
          userSelect: 'none',
          '& > div': {
            display: 'inline-block',
          },
          '& dt': {
            textStyle: 'Text/S/Regular',
            color: 'content.default.level-2',
            mb: 2,
          },
          '& dd': {
            textStyle: 'Text/M/Monospace/Regular',
            color: 'content.default.level-5',
          },
          '& > div:has(dt)': {
            minW: 68,
            mr: 16,
          },
        })}
        ref={scrollRef}
        {...mouseEvents}
      >
        <div ref={leftRef} className={css({ w: 1 })} />
        {children}
        <div ref={rightRef} className={css({ w: 1 })} />
      </dl>
      {isScrollable && !isVisibleRight && (
        <button
          type='button'
          value='right'
          onClick={onClickScroll}
          className={css(scrollArrowCSS, { right: 0, transform: 'rotate(180deg)' })}
        >
          <ChevronLeftIcon size={16} />
        </button>
      )}
    </div>
  );
};

const scrollArrowCSS = css.raw({
  zIndex: 1,
  pos: 'absolute',
  top: 0,
  bottom: 0,
  w: 20,
  background: 'linear-gradient(90deg, {colors.background.default.level-0} 32%, rgba(0, 0, 0, 0%) 120%)',
  '& > svg': {
    color: 'content.default.level-1',
  },
});
